// TODO(RBAC, parent #641 #647): 権限チェックは別途実装
import { useMemo, useState } from 'react'

import { PlanSubmissionKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  type WorkflowCase,
  WorkflowCaseStatus,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useParams, useSearchParams } from 'react-router'

import {
  Alert,
  Box,
  Flex,
  HStack,
  Link,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaCircleCheckSolid } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { DeleteCaseDialog } from '~/components/common/DeleteCaseDialog'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { PlanSubmissionStatusBadge } from '~/components/common/PlanSubmissionStatusBadge'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowNotFoundView } from '~/components/common/WorkflowNotFoundView'
import { iconSize } from '~/constants/iconSize'
import { useListMembers } from '~/hooks/member/useListMembers'
import { WORKFLOW_CASE_REFETCH_INTERVAL_MS } from '~/hooks/workflowCase/refetchInterval'
import { useGetWorkflowCase } from '~/hooks/workflowCase/useGetWorkflowCase'
import { resolveLinkedPage } from '~/models/linkedPage'
import {
  STATUS_BADGE_LABEL_KEYS,
  deriveStatusBadge,
} from '~/models/planSubmission/statusBadge'
import { hasActiveSubStepFocusSignal } from '~/models/workflowCaseFocusSignal'
import { isWorkflowCaseNotFoundError } from '~/models/workflowCaseNotFound'
import { BigStepAccordion } from '~/pages/WorkflowCaseDetail/components/BigStepAccordion'
import { CaseActionBar } from '~/pages/WorkflowCaseDetail/components/CaseActionBar'
import { CaseHeader } from '~/pages/WorkflowCaseDetail/components/CaseHeader'
import { WizardStepper } from '~/pages/WorkflowCaseDetail/components/WizardStepper'
import { useListWorkflowCaseRetainedArtifacts } from '~/pages/WorkflowCaseDetail/hooks/useListWorkflowCaseRetainedArtifacts'
import {
  readOpenBigSteps,
  shouldOpenBigStep,
  writeOpenBigSteps,
} from '~/pages/WorkflowCaseDetail/models/openBigSteps'
import {
  activeSubStepOf,
  isBigStepComplete,
  isBigStepUnlocked,
  isWorkflowCaseReadonly,
} from '~/pages/WorkflowCaseDetail/models/workflowCaseDetail'
import { routePaths } from '~/routes/routePaths'

export const WorkflowCaseDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { caseId } = useParams<{ caseId: string }>()
  const [searchParams] = useSearchParams()

  // 削除確認ダイアログは open した時点の case を snapshot する (一覧
  // `WorkflowCases/index.tsx` の `deletingRow` と同形)。live な `wfCase` を渡すと、
  // 詳細に入った AC-WFW-039 の 30 秒 polling がダイアログを開いたまま tick を跨いだ
  // 際に `expectedUpdatedAt` の基準 `updatedAt` を黙って最新化し、他ユーザーの更新を
  // 楽観ロック (ABORTED) で弾けず不可逆な削除が通ってしまうため。
  const [deletingCase, setDeletingCase] = useState<WorkflowCase | undefined>(
    undefined,
  )

  // AC-WFW-039 (Design Note `tmp/designing-feature/workflow-auto-update.md`
  // D2/D4): 詳細表示中は WORKFLOW_CASE_REFETCH_INTERVAL_MS (30 秒) 間隔で自動再
  // 取得し、他ユーザーの操作 (サブステップ完了・承認) を手動リロードなしで反映
  // する。 この option を渡すのは本呼び出しからのみ — `useGetWorkflowCase` を
  // `useCurrentWorkflowCase` 経由で共有する 16+ ページ (read-only 判定用の付帯
  // 取得) へ polling を波及させない。
  const { data, isLoading, isError, error } = useGetWorkflowCase(caseId, {
    throwOnError: false,
    refetchInterval: WORKFLOW_CASE_REFETCH_INTERVAL_MS,
  })
  const wfCase = data?.workflowCase
  const { data: retainedArtifacts } = useListWorkflowCaseRetainedArtifacts(
    caseId,
    wfCase?.status === WorkflowCaseStatus.DELETED,
  )
  const { data: membersData } = useListMembers()
  const members = membersData?.members ?? []

  const retainedDemandLink =
    wfCase !== undefined
      ? resolveLinkedPage(LinkedPageKind.DEMAND_FORECAST_DAILY, wfCase.id)
      : null

  // 一覧の要承認・要対応ボタンからの focus 遷移 (spec AC-WFW-001 §243、
  // 2026-08-19 改訂 D5)。signal が無い、または終端ケース (アクティブサブステップ
  // 無し) の場合は undefined のままで、従来どおり自動展開しない。
  const focusTarget = useMemo(
    () =>
      wfCase !== undefined && hasActiveSubStepFocusSignal(searchParams)
        ? activeSubStepOf(wfCase)
        : undefined,
    [wfCase, searchParams],
  )

  const sortedBigSteps = useMemo(
    () =>
      wfCase !== undefined
        ? wfCase.bigSteps
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
        : [],
    [wfCase],
  )

  /**
   * 未保存時に画面へ出ている開閉 (= 既定)。先頭の大ステップと、一覧の
   * 要承認・要対応から遷移した focus 対象を開く。1 つ操作された時の起点に使う。
   */
  const defaultOpenBigStepOrders = useMemo(
    () =>
      new Set(
        sortedBigSteps
          .filter(
            (bs, idx) =>
              idx === 0 || focusTarget?.bigStepDisplayOrder === bs.displayOrder,
          )
          .map((bs) => bs.displayOrder),
      ),
    [sortedBigSteps, focusTarget],
  )

  /**
   * 大ステップの開閉を、画面を離れて戻る間だけ覚えておく (#7067)。
   * 読みは初回のみ (`useState` の初期化関数)。以後は本 component の state が
   * 真とし、変化のたびに sessionStorage へ写す。判断は models/openBigSteps が SoT。
   */
  const caseIdForOpenState = wfCase?.id ?? ''
  /**
   * この mount 中に操作された開閉。未操作なら `undefined` で、保存値を使う。
   *
   * **保存値は `useState` の初期化で読まない。** 初回 render では取得が終わって
   * おらず `caseId` が空で、初期化関数は再実行されないため保存値を取り逃がす
   * (実際にそれで復元されなかった)。`caseId` が確定してから読む。
   */
  const [openOverride, setOpenOverride] = useState<
    { caseId: string; open: Set<number> } | undefined
  >(undefined)
  const savedOpenBigSteps = useMemo(() => {
    if (caseIdForOpenState === '') return undefined
    // **override は caseId と対で持つ。** 詳細 route は `:caseId` が変わっても
    // 同じ component が生き続けるため、ケースを移った時に前ケースの集合を
    // そのまま使うと、誤った開閉で表示したうえ移動先の key へ上書きしてしまう
    // (レビュー指摘)。
    if (openOverride?.caseId === caseIdForOpenState) return openOverride.open
    return readOpenBigSteps(caseIdForOpenState)
  }, [openOverride, caseIdForOpenState])
  const handleBigStepOpenChange = (
    displayOrder: number,
    isOpen: boolean,
  ): void => {
    if (caseIdForOpenState === '') return
    setOpenOverride((prev) => {
      const prevOpen =
        prev?.caseId === caseIdForOpenState ? prev.open : undefined
      // 未保存 (= 既定で表示中) の状態から 1 つ操作された時は、現在の見た目を
      // 起点にする必要がある。ここは「操作されたものだけ」を積むのではなく、
      // 既定を含めた現在の開閉集合を親が持ち直す形にする。
      const base = new Set(
        prevOpen ??
          readOpenBigSteps(caseIdForOpenState) ??
          defaultOpenBigStepOrders,
      )
      if (isOpen) base.add(displayOrder)
      else base.delete(displayOrder)
      writeOpenBigSteps(caseIdForOpenState, base)
      return { caseId: caseIdForOpenState, open: base }
    })
  }

  const totalSubSteps = useMemo(
    () => sortedBigSteps.reduce((sum, bs) => sum + bs.subSteps.length, 0),
    [sortedBigSteps],
  )

  const doneSubSteps = useMemo(
    () =>
      sortedBigSteps.reduce(
        (sum, bs) => sum + bs.subSteps.filter((ss) => ss.isCompleted).length,
        0,
      ),
    [sortedBigSteps],
  )

  // AC-10: 完了 (COMPLETE) または削除済み (DELETED、不可逆) で read-only。
  const isReadonly =
    wfCase !== undefined ? isWorkflowCaseReadonly(wfCase) : false

  // 見つからない (存在しない・他組織・形式不正の ID) 時は AC-WFW-040 の表示、
  // それ以外の取得失敗 (通信エラー・サーバーエラー) は AC-WFW-036 の文言。
  // 再試行しても直らない not-found に「時間をおいて再度お試しください」 を出さない。
  // error 無しで data が空の応答も、開いた ID のワークフローが無いので not-found 側。
  const isWorkflowNotFound =
    isWorkflowCaseNotFoundError(error) || (!isError && wfCase === undefined)

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('common:nav.workflow') },
        {
          label: t('common:route.workflowCases'),
          to: routePaths.workflowCases.path,
        },
        {
          label: t('page:workflowCaseDetail.title'),
          bold: true,
        },
      ]}
    >
      <PageBody>
        {/* 画面全体スクロール (#5660 Des 裁定 ①「B = 全体スクロール + 操作バーだけ
            sticky」)。 design `case-detail.html` の `.cd-action-bar` が
            `position: sticky; bottom: 0` で同じ形。
            従来は大ステップアコーディオンだけが PageScrollArea の中にあり、 リード文・
            ケース基本情報・ステッパー・注記は固定領域に居座っていたため、 実際に動かせる
            範囲が狭く「全体を見にくい」状態だった (デザイナー指摘 2026-08-17)。
            リード文を含む本文をまとめてスクロール領域へ入れ、 操作バーは sticky で下端に
            残す。 共通シェル (PageBody の height:100%+overflow:hidden、 PageScrollArea
            は 1 ページ 1 箇所) はそのまま守る。 縦 gap は PageBody と同じ 16px を内側の
            Flex で補う (PageScrollArea 自体は gap を持たないため)。 */}
        <RegionLoading
          flexDirection="column"
          flex="1"
          display="flex"
          minHeight={isLoading && wfCase === undefined ? '200px' : 0}
          isLoading={isLoading && wfCase === undefined}
        >
          <PageScrollArea>
            <Flex flexDirection="column" gap={4}>
              {/* design/demand/case-detail.html `.page-description` 相当のリード文。
                loading / error でも出したいので条件分岐の外に置く。 */}
              {/* 画面冒頭の説明文は共通の PageIntro (UI 辞書 components/page-layout-utilities.md §1。
                  色は high-emphasis 固定) */}
              <PageIntro
                description={t('page:workflowCaseDetail.description')}
              />

              {isLoading && wfCase === undefined ? null : wfCase ===
                undefined ? (
                isWorkflowNotFound ? (
                  <WorkflowNotFoundView />
                ) : (
                  <CompactAlert
                    status="error"
                    title={t('page:workflowCaseDetail.error.fetchFailed')}
                  />
                )
              ) : (
                <>
                  {/* 完了済み / 削除済み 通知 (design/demand/case-detail.html
                    #cd-completed-banner 相当。 BFF 導出ステータス (wfCase.status) で
                    文言・トーンを出し分ける。 completedAt の有無だけでは削除済み
                    (DELETED) ケースも「完了済み」と誤表示するため status を見る) */}
                  {isReadonly &&
                    (wfCase.status === WorkflowCaseStatus.DELETED ? (
                      // 見出しだけ。 実施者・日時はヘッダーのメタ情報 (削除日時・削除者) に置く
                      // (2026-10-20 デザイナー指示: Alert に個別の値を並べない。 UI 辞書 alert-placement.md)
                      <Alert
                        status="error"
                        title={t(
                          'page:workflowCaseDetail.readonly.deletedNotice',
                        )}
                      />
                    ) : (
                      <Alert
                        status="success"
                        title={t(
                          'page:workflowCaseDetail.readonly.completedNotice',
                        )}
                      />
                    ))}

                  {wfCase.status === WorkflowCaseStatus.DELETED &&
                    retainedArtifacts !== undefined &&
                    (retainedArtifacts.hasConfirmedDemandForecast ||
                      retainedArtifacts.retainedPlanSubmissions.length > 0) && (
                      <Box padding={4} borderWidth="1px" borderRadius="md">
                        <Text fontWeight="semibold">
                          {t('page:workflowCaseDetail.retainedArtifacts.title')}
                        </Text>
                        {retainedArtifacts.hasConfirmedDemandForecast &&
                          retainedDemandLink !== null && (
                            <Link asChild>
                              <RouterLink to={retainedDemandLink.href}>
                                {t(
                                  'page:workflowCaseDetail.retainedArtifacts.confirmedDemand',
                                )}
                              </RouterLink>
                            </Link>
                          )}
                        {retainedArtifacts.retainedPlanSubmissions.map(
                          (plan) => {
                            const linkedPage = resolveLinkedPage(
                              plan.kind === PlanSubmissionKind.GENERATION_SALE
                                ? LinkedPageKind.PLAN_GEN_SALES
                                : LinkedPageKind.PLAN_PROCUREMENT,
                              wfCase.id,
                              plan.id,
                            )
                            const badge = deriveStatusBadge(plan.status)
                            if (linkedPage === null) return null
                            return (
                              <HStack key={plan.id} gap={2}>
                                <Link asChild>
                                  <RouterLink to={linkedPage.href}>
                                    {t(
                                      plan.kind ===
                                        PlanSubmissionKind.GENERATION_SALE
                                        ? 'page:workflowCaseDetail.retainedArtifacts.generationSalePlan'
                                        : 'page:workflowCaseDetail.retainedArtifacts.demandProcurementPlan',
                                      { version: plan.version },
                                    )}
                                  </RouterLink>
                                </Link>
                                <PlanSubmissionStatusBadge
                                  hasWarning={badge.hasWarning}
                                  labelKeys={STATUS_BADGE_LABEL_KEYS}
                                  variant={badge.variant}
                                />
                              </HStack>
                            )
                          },
                        )}
                      </Box>
                    )}

                  {/* ケース基本情報ヘッダー (spec AC-010a/b: 承認フロー設定への導線を内包) */}
                  <CaseHeader
                    doneSubSteps={doneSubSteps}
                    isReadonly={isReadonly}
                    totalSubSteps={totalSubSteps}
                    wfCase={wfCase}
                  />

                  {/* ウィザード型ステッパー */}
                  <WizardStepper bigSteps={wfCase.bigSteps} />

                  {/* 進捗自動保存の注記 (design: 「進捗は自動保存されます」ヒント) */}
                  <Box alignItems="center" gap="6px" display="flex">
                    <FaCircleCheckSolid
                      boxSize={iconSize.guideButton}
                      color="Text.midEmphasis"
                    />
                    <Text color="Text.midEmphasis" fontSize="sm">
                      {t(
                        'page:workflowCaseDetail.stepper.autoSaveNoticePrefix',
                      )}{' '}
                      <Text as="span" fontWeight="bold">
                        {t(
                          'page:workflowCaseDetail.stepper.autoSaveNoticeBold',
                        )}
                      </Text>{' '}
                      {t(
                        'page:workflowCaseDetail.stepper.autoSaveNoticeSuffix',
                      )}
                    </Text>
                  </Box>

                  {/* 大ステップアコーディオン */}
                  <div>
                    {sortedBigSteps.map((bs, idx) => {
                      const prevComplete =
                        idx > 0
                          ? isBigStepComplete(sortedBigSteps[idx - 1].subSteps)
                          : false
                      const isLocked = !isBigStepUnlocked(idx, prevComplete)
                      const isFocusedBigStep =
                        focusTarget?.bigStepDisplayOrder === bs.displayOrder
                      return (
                        <BigStepAccordion
                          // ケースを移ったら初期表示を取り直す必要がある
                          // (`defaultOpen` は初回 mount でしか効かないため、
                          // key にケース ID を含めて remount させる)。
                          key={`${wfCase.id}-${bs.displayOrder}`}
                          bigStep={bs}
                          caseId={wfCase.id}
                          defaultOpen={shouldOpenBigStep({
                            displayOrder: bs.displayOrder,
                            isFirst: idx === 0,
                            isFocused: isFocusedBigStep,
                            saved: savedOpenBigSteps,
                          })}
                          focusSubStepDisplayOrder={
                            isFocusedBigStep
                              ? focusTarget?.subStepDisplayOrder
                              : undefined
                          }
                          isLocked={isLocked}
                          isReadonly={isReadonly}
                          members={members}
                          onOpenChange={(open) => {
                            handleBigStepOpenChange(bs.displayOrder, open)
                          }}
                          wfCase={wfCase}
                        />
                      )
                    })}
                  </div>
                </>
              )}
            </Flex>

            {/* 画面下部 action bar (spec AC-WFW-008: ワークフロー完了は全サブステップ
              完了で自動的に遷移し、手動で「WF 完了を取消す」ボタンは提供しない。
              ただし最終サブステップに対する UndoSubStepCompletion (AC-WFW-005d)
              では backend の WithAutoCompleteCleared で自動完了スタンプが
              クリアされ WF ステータスは「進行中」 に戻る)。
              スクロール領域の中に置き、 自身の position:sticky で下端に貼り付ける
              (#5660。 8080 の `.cd-action-bar` と同じ形)。 */}
            {/* TODO(RBAC, parent #641 #647): 削除は権限チェック後に制限 */}
            {wfCase !== undefined && (
              <CaseActionBar
                doneSubSteps={doneSubSteps}
                onDeleteClick={() => {
                  setDeletingCase(wfCase)
                }}
                totalSubSteps={totalSubSteps}
                wfCase={wfCase}
              />
            )}
          </PageScrollArea>
        </RegionLoading>

        {/* 削除確認ダイアログ (spec AC-003f/g、workflow-delete-transition design)。
            削除成功後は詳細に留まり削除バナー + 全操作 disabled へ (AC-11、一覧への
            強制遷移はしない)。 */}
        <DeleteCaseDialog
          row={deletingCase}
          onClose={() => {
            setDeletingCase(undefined)
          }}
        />
      </PageBody>
    </PageLayout>
  )
}
